'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherAlertCircle, FeatherArrowLeft, FeatherCheckCircle, FeatherInfo, FeatherLock } from '@subframe/core';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { Alert, Badge, Button, ConfirmationModal, EmptyState, LazyLoader, PageHeader_Main, StepperVertical } from '@/shared/components/ui';
import { useAssinaturaSimulada } from '../hooks/useAssinaturaSimulada';
import type { AssinaturaSimuladaProps, ResultadoDaAssinatura } from '../types';
import { CONFIRMACAO_DA_ACAO_DE_ASSINATURA, RESULTADO_REGISTRADO_DA_ASSINATURA, ROTULO_DA_ACAO_DE_ASSINATURA, ROTULO_DO_PROVEDOR, rotuloDoEstado } from '../utils/rotulosDaSimulacao';
import { DocumentoDaAssinaturaDeTeste } from './DocumentoDaAssinaturaDeTeste';
const ROTA_DA_CONVERSA_DO_CLIENTE = '/negociacoes/simulacao';
const LARGURA = 'w-full max-w-[1160px]';
const CARTAO = 'bg-default-background flex min-w-0 flex-col rounded-[16px] border border-solid border-neutral-200 p-6';

/**
 * TELA-LEXI-SIM-002, montada só com componentes da plataforma (decisão 2-B do Kleiton; não há
 * mock). `data-dd-privacy="mask"`: o nome e o texto do documento de teste são conteúdo do agente
 * e não vão para o replay do RUM.
 */
export const AssinaturaSimulada = t0 => {
  const $ = _c(48);
  const {
    assinaturaId,
    rotaDaConversa: t1
  } = t0;
  const rotaDaConversa = t1 === undefined ? ROTA_DA_CONVERSA_DO_CLIENTE : t1;
  const router = useRouter();
  const tela = useAssinaturaSimulada(assinaturaId);
  const [confirmando, setConfirmando] = useState(null);
  const {
    assinatura,
    situacao
  } = tela;
  const decidida = situacao === "decidida";
  const confirmacao = confirmando ? CONFIRMACAO_DA_ACAO_DE_ASSINATURA[confirmando] : null;
  let t2;
  if ($[0] !== rotaDaConversa || $[1] !== router) {
    t2 = () => router.push(rotaDaConversa);
    $[0] = rotaDaConversa;
    $[1] = router;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const voltarAConversa = t2;
  let t3;
  if ($[3] !== confirmando || $[4] !== tela) {
    t3 = () => {
      if (!confirmando) {
        return;
      }
      setConfirmando(null);
      tela.registrar(confirmando);
    };
    $[3] = confirmando;
    $[4] = tela;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const confirmar = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <FeatherArrowLeft />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== voltarAConversa) {
    t5 = <Button variant="neutral-secondary" icon={t4} onClick={voltarAConversa}>Voltar à conversa</Button>;
    $[7] = voltarAConversa;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const botaoDeVoltar = t5;
  const t6 = assinatura ? `Etapa do provedor ${ROTULO_DO_PROVEDOR[assinatura.provedor]} na simulação.` : "Etapa do provedor de assinatura na simula\xE7\xE3o.";
  let t7;
  if ($[9] !== botaoDeVoltar || $[10] !== t6) {
    t7 = <PageHeader_Main title="Assinatura de teste" subtitle={t6} actions={botaoDeVoltar} className={`${LARGURA} border-0 pb-0`} />;
    $[9] = botaoDeVoltar;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Alert className={`${LARGURA} mt-6`} variant="warning" tone="filled" icon={<FeatherInfo />} title={"Simula\xE7\xE3o"} description={"Esta assinatura e este documento s\xE3o de teste: n\xE3o produzem assinatura v\xE1lida nem efeitos em negocia\xE7\xE3o real."} />;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== situacao) {
    t9 = situacao === "carregando" && <div className={`${LARGURA} mt-6 min-h-[240px]`} aria-label="Carregando a assinatura"><LazyLoader /></div>;
    $[13] = situacao;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== botaoDeVoltar || $[16] !== situacao) {
    t10 = situacao === "bloqueado" && <EmptyState className={`${LARGURA} ${CARTAO} mt-6 items-center`} icon={<FeatherLock />} title={"Este link de assinatura n\xE3o est\xE1 dispon\xEDvel"} description={"O link expirou, j\xE1 foi usado, pertence a outra simula\xE7\xE3o ou voc\xEA perdeu o acesso a ela."} action={botaoDeVoltar} />;
    $[15] = botaoDeVoltar;
    $[16] = situacao;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== situacao || $[19] !== tela.abrirDeNovo || $[20] !== tela.erro?.mensagem) {
    t11 = situacao === "erro" && <Alert role="alert" className={`${LARGURA} mt-6`} variant="error" tone="filled" icon={<FeatherAlertCircle />} title={"N\xE3o foi poss\xEDvel carregar a assinatura de teste."} description={tela.erro?.mensagem} actions={<Button variant="neutral-secondary" size="small" onClick={tela.abrirDeNovo}>Tentar novamente</Button>} />;
    $[18] = situacao;
    $[19] = tela.abrirDeNovo;
    $[20] = tela.erro?.mensagem;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== assinatura || $[23] !== decidida || $[24] !== situacao || $[25] !== tela.acoes || $[26] !== tela.erroDoRegistro || $[27] !== tela.podeRepetirRegistro || $[28] !== tela.registrando || $[29] !== tela.repetirRegistro || $[30] !== tela.simulacaoId || $[31] !== tela.superficie || $[32] !== voltarAConversa) {
    t12 = assinatura && (situacao === "pendente" || decidida) && <div className={`${LARGURA} mt-6 grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(340px,1fr)_390px]`}><DocumentoDaAssinaturaDeTeste superficie={tela.superficie} simulacaoId={tela.simulacaoId} arquivoId={assinatura.arquivoId} nomeDocumento={assinatura.nomeDocumento} /><section aria-labelledby="etapas-da-assinatura" className={CARTAO}><div className="flex flex-wrap items-center justify-between gap-2"><h2 id="etapas-da-assinatura" className="text-heading-3 font-heading-3 text-default-font">Etapas da assinatura</h2><Badge variant={decidida ? "success" : "neutral"}>{rotuloDoEstado(assinatura.estado)}</Badge></div><StepperVertical className="mt-5"><StepperVertical.Step variant="completed" stepNumber="1" label="Documento de teste" description="Confira o documento que o agente enviou." /><StepperVertical.Step variant={decidida ? "completed" : "active"} stepNumber="2" label={`Resultado na ${ROTULO_DO_PROVEDOR[assinatura.provedor]}`} description="Escolha o que acontece com a assinatura do cliente." /><StepperVertical.Step variant={decidida ? "active" : "default"} stepNumber="3" label={"Volta \xE0 conversa"} description="O agente recebe o resultado e segue o flow." lastStep={true} /></StepperVertical>{situacao === "pendente" && <div className="flex flex-col gap-2">{tela.acoes.map(acao => <Button key={acao} className="w-full justify-center" variant={acao === "CONCLUIDA" ? "brand-primary" : "neutral-secondary"} disabled={tela.registrando !== null} loading={tela.registrando === acao} onClick={() => setConfirmando(acao)}>{ROTULO_DA_ACAO_DE_ASSINATURA[acao]}</Button>)}{tela.acoes.length === 0 && <p className="text-body text-subtext-color">Nenhuma ação disponível para esta assinatura.</p>}</div>}{tela.erroDoRegistro && <Alert role="alert" className="mt-4" variant="error" tone="filled" icon={<FeatherAlertCircle />} title={"O resultado n\xE3o foi registrado."} description={tela.erroDoRegistro.mensagem} actions={tela.podeRepetirRegistro ? <Button variant="neutral-secondary" size="small" onClick={tela.repetirRegistro}>Tentar novamente</Button> : undefined} />}{decidida && assinatura.estado !== "PENDENTE" && <div className="flex flex-col gap-4"><Alert role="status" variant={assinatura.estado === "CONCLUIDA" ? "success" : "neutral"} tone="filled" icon={<FeatherCheckCircle />} title={RESULTADO_REGISTRADO_DA_ASSINATURA[assinatura.estado]} description={"O resultado foi registrado na simula\xE7\xE3o. Volte \xE0 conversa para ver como o agente segue."} /><Button className="w-full justify-center" onClick={voltarAConversa}>Voltar à conversa</Button></div>}</section></div>;
    $[22] = assinatura;
    $[23] = decidida;
    $[24] = situacao;
    $[25] = tela.acoes;
    $[26] = tela.erroDoRegistro;
    $[27] = tela.podeRepetirRegistro;
    $[28] = tela.registrando;
    $[29] = tela.repetirRegistro;
    $[30] = tela.simulacaoId;
    $[31] = tela.superficie;
    $[32] = voltarAConversa;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const t13 = confirmacao !== null;
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = aberto => {
      if (!aberto) {
        setConfirmando(null);
      }
    };
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const t15 = confirmacao?.titulo ?? "";
  const t16 = confirmacao?.descricao ?? "";
  const t17 = confirmacao?.confirmar;
  let t18;
  if ($[35] !== confirmar || $[36] !== t13 || $[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <ConfirmationModal isOpen={t13} onOpenChange={t14} title={t15} description={t16} confirmText={t17} cancelText="Voltar" onConfirm={confirmar} />;
    $[35] = confirmar;
    $[36] = t13;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t12 || $[44] !== t18 || $[45] !== t7 || $[46] !== t9) {
    t19 = <div data-dd-privacy="mask" className="flex h-full w-full flex-col overflow-auto p-6 md:p-9">{t7}{t8}{t9}{t10}{t11}{t12}{t18}</div>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t12;
    $[44] = t18;
    $[45] = t7;
    $[46] = t9;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  return t19;
};